import type {
  ISODateRange,
  ISODateString,
  PacingBlockV2FromDB
} from 'shared/src/date-string-utils/types';
import type { PacingType } from 'shared/src/domain/line-item/line-item-types';
import {
  today as getToday,
  formatDate,
  subtractDays,
  startOfMonth,
  endOfMonth,
  addMonths,
  isBefore,
  isAfter,
  daysBetween
} from 'shared/src/date-string-utils/date-utils';

// Feedback
// Get rid of the generated // Types like comments
// Order of members in the file - types, then public functions, then private function in call order. We have some instructions on this in claude.md, but it sometimes ignores

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TimeFrameMode = 'lifetime' | 'pacing_blocks' | 'months' | 'last_7_days';

export type TimeFramePacingBlock = {
  label: string;
  from: ISODateString;
  to: ISODateString;
  index: number;
  status: 'past' | 'current' | 'future';
};

export type TimeFrameMonth = {
  label: string;
  from: ISODateString;
  to: ISODateString;
  status: 'past' | 'current' | 'future';
};

export type TimeFrameSelection = {
  timeFrameMode: TimeFrameMode;
  dateRange: ISODateRange;
  subIndex: number | null;
};

export type TimeFrameVisibilityConfig = {
  lifetime?: boolean;
  pacing_blocks?: boolean;
  months?: boolean;
  last_7_days?: boolean;
};

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function clipDate(date: ISODateString, min: ISODateString, max: ISODateString): ISODateString {
  if (isBefore(date, min)) return min;
  if (isAfter(date, max)) return max;
  return date;
}

function computeStatus(
  from: ISODateString,
  to: ISODateString,
  todayISO: ISODateString
): 'past' | 'current' | 'future' {
  if (isAfter(from, todayISO)) return 'future';
  if (isBefore(to, todayISO)) return 'past';
  return 'current';
}

// ---------------------------------------------------------------------------
// Build pacing blocks
// ---------------------------------------------------------------------------

export function buildPacingBlocks(
  pacingDetails: PacingBlockV2FromDB[] | null | undefined,
  lineItemDates: ISODateRange,
  todayISO: ISODateString = getToday()
): TimeFramePacingBlock[] {
  if (!pacingDetails || pacingDetails.length === 0) return [];

  return pacingDetails.map((block, index) => {
    const from = clipDate(block.dates.from, lineItemDates.from, lineItemDates.to);
    const to = clipDate(block.dates.to, lineItemDates.from, lineItemDates.to);
    return {
      label: `Block ${index + 1}`,
      from,
      to,
      index,
      status: computeStatus(from, to, todayISO)
    };
  });
}

// ---------------------------------------------------------------------------
// Build months
// ---------------------------------------------------------------------------

export function buildMonths(
  lineItemDates: ISODateRange,
  todayISO: ISODateString = getToday()
): TimeFrameMonth[] {
  const months: TimeFrameMonth[] = [];
  let cursor = startOfMonth(lineItemDates.from);

  while (!isAfter(cursor, lineItemDates.to)) {
    const monthStart = clipDate(cursor, lineItemDates.from, lineItemDates.to);
    const monthEnd = clipDate(endOfMonth(cursor), lineItemDates.from, lineItemDates.to);

    months.push({
      label: formatDate(cursor, "MMM ''yy"),
      from: monthStart,
      to: monthEnd,
      status: computeStatus(monthStart, monthEnd, todayISO)
    });

    cursor = addMonths(cursor, 1);
  }

  return months;
}

// ---------------------------------------------------------------------------
// Calculate date range for a selection
// ---------------------------------------------------------------------------

export function calculateTimeFrameDateRange(
  timeFrameMode: TimeFrameMode,
  lineItemDates: ISODateRange,
  blocks: TimeFramePacingBlock[],
  months: TimeFrameMonth[],
  subIndex: number | null,
  todayISO: ISODateString = getToday()
): ISODateRange {
  switch (timeFrameMode) {
    case 'lifetime':
      return { from: lineItemDates.from, to: lineItemDates.to };

    case 'pacing_blocks': {
      const block = subIndex !== null ? blocks[subIndex] : undefined;
      if (block) return { from: block.from, to: block.to };
      return { from: lineItemDates.from, to: lineItemDates.to };
    }

    case 'months': {
      const month = subIndex !== null ? months[subIndex] : undefined;
      if (month) return { from: month.from, to: month.to };
      return { from: lineItemDates.from, to: lineItemDates.to };
    }

    case 'last_7_days': {
      const yesterday = subtractDays(todayISO, 1);
      const sevenDaysAgo = subtractDays(yesterday, 6);
      const from = clipDate(sevenDaysAgo, lineItemDates.from, lineItemDates.to);
      const to = clipDate(yesterday, lineItemDates.from, lineItemDates.to);
      return { from, to };
    }
  }
}

// ---------------------------------------------------------------------------
// Find current index helpers
// ---------------------------------------------------------------------------

export function getCurrentBlockIndex(blocks: TimeFramePacingBlock[]): number {
  const currentIdx = blocks.findIndex(b => b.status === 'current');
  if (currentIdx !== -1) return currentIdx;

  // Fallback: last past block
  const lastPastIdx = blocks.findLastIndex(b => b.status === 'past');
  return lastPastIdx !== -1 ? lastPastIdx : 0;
}

export function getCurrentMonthIndex(months: TimeFrameMonth[]): number {
  const currentIdx = months.findIndex(m => m.status === 'current');
  if (currentIdx !== -1) return currentIdx;

  const lastPastIdx = months.findLastIndex(m => m.status === 'past');
  return lastPastIdx !== -1 ? lastPastIdx : 0;
}

// ---------------------------------------------------------------------------
// Default selection
// ---------------------------------------------------------------------------

export function getDefaultSelection(
  pacingType: PacingType,
  blocks: TimeFramePacingBlock[],
  months: TimeFrameMonth[],
  lineItemDates: ISODateRange,
  todayISO: ISODateString = getToday()
): TimeFrameSelection {
  if (pacingType === 'custom' && blocks.length > 0) {
    const subIndex = getCurrentBlockIndex(blocks);
    return {
      timeFrameMode: 'pacing_blocks',
      subIndex,
      dateRange: calculateTimeFrameDateRange(
        'pacing_blocks',
        lineItemDates,
        blocks,
        months,
        subIndex,
        todayISO
      )
    };
  }

  // lifetime → default to current month
  const subIndex = getCurrentMonthIndex(months);
  return {
    timeFrameMode: 'months',
    subIndex,
    dateRange: calculateTimeFrameDateRange(
      'months',
      lineItemDates,
      blocks,
      months,
      subIndex,
      todayISO
    )
  };
}

// ---------------------------------------------------------------------------
// Default time frame (convenience for initializing state)
// ---------------------------------------------------------------------------

type DefaultTimeFrameInput = {
  pacing_type: PacingType;
  pacing_details?: PacingBlockV2FromDB[] | null | undefined;
  dates: ISODateRange;
};

export function getDefaultTimeFrame(lineItem: DefaultTimeFrameInput): {
  dateRange: ISODateRange;
  timeFrameMode: TimeFrameMode;
} {
  const todayISO = getToday();
  const blocks = buildPacingBlocks(lineItem.pacing_details, lineItem.dates, todayISO);
  const months = buildMonths(lineItem.dates, todayISO);
  const selection = getDefaultSelection(
    lineItem.pacing_type,
    blocks,
    months,
    lineItem.dates,
    todayISO
  );
  return { dateRange: selection.dateRange, timeFrameMode: selection.timeFrameMode };
}

export function getDefaultDateRange(lineItem: DefaultTimeFrameInput): ISODateRange {
  return getDefaultTimeFrame(lineItem).dateRange;
}

// ---------------------------------------------------------------------------
// Format date display
// ---------------------------------------------------------------------------

export function formatDateDisplay(date: ISODateString): string {
  return formatDate(date, "MMM d ''yy");
}

// ---------------------------------------------------------------------------
// Day count
// ---------------------------------------------------------------------------

export function dayCount(range: ISODateRange): number {
  return daysBetween(range.from, range.to);
}
